import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AanmeldShell } from "@/components/AanmeldShell";
import { ListDetail } from "@/components/ListDetail";
import { Card, CardContent } from "@/components/ui/card";
import { GeenKorpsKaart } from "@/features/inschrijving/GeenKorpsKaart";
import { InschrijvingDetail } from "@/features/inschrijving/InschrijvingDetail";
import { KorpsenTable } from "@/features/ploegen/KorpsenTable";
import { useMe } from "@/features/session/useMe";
import { useSeizoenen } from "@/features/session/useSeizoenen";
import { keepScrollPosition } from "@jbnl/shared-ui";

/**
 * Two shapes, and not a preference: a korpscontact holds one korps, so a list of it is a click
 * that can only go one way. A regiocontact gets the Backoffice's list-detail, because twenty
 * korpsen do not fit above a table and the chosen one has to stay in sight.
 */
export function PloegenPage() {
  const { t } = useTranslation(["ploegen", "common"]);
  const { data: me } = useMe();
  const { data: seizoenen } = useSeizoenen();
  const {
    korps: korpsId,
    pagina,
    periode: periodeId,
  } = useSearch({ from: "/ploegen" });
  const navigate = useNavigate({ from: "/ploegen" });

  const seizoen = seizoenen?.[0];

  // Read out here as well as inside the shell, because the frame itself widens for the second
  // pane - and the shell has to know that before it draws anything.
  const meerdere = (me?.korpsen.length ?? 0) > 1;

  const kies = (id?: string) =>
    void navigate({
      ...keepScrollPosition,
      search: (huidig) => ({ ...huidig, korps: id }),
    });

  const kiesPeriode = (id: string) =>
    void navigate({
      ...keepScrollPosition,
      search: (huidig) => ({ ...huidig, periode: id }),
    });

  return (
    <AanmeldShell
      wide={meerdere}
      titel={t("ploegen:page.title")}
      uitleg={
        seizoen
          ? t("ploegen:page.seizoen", { naam: seizoen.name })
          : t("ploegen:page.geenSeizoen")
      }
    >
      {(session) => {
        if (session.korpsen.length === 0) {
          return <GeenKorpsKaart />;
        }

        if (!seizoen) {
          return null;
        }

        // One korps is not a choice: it is picked without asking, and the URL never names it.
        if (session.korpsen.length === 1) {
          return (
            <Card>
              <CardContent className="pt-6">
                <InschrijvingDetail
                  seizoenId={seizoen.id}
                  korps={session.korpsen[0]}
                  named={false}
                  periodeId={periodeId}
                  onKiesPeriode={kiesPeriode}
                />
              </CardContent>
            </Card>
          );
        }

        const korps = session.korpsen.find((k) => k.id === korpsId);

        return (
          <ListDetail
            selected={Boolean(korps)}
            backLabel={t("ploegen:korpsen.terug")}
            onBack={() => kies()}
            list={
              <KorpsenTable
                korpsen={session.korpsen}
                korpsId={korps?.id}
                onKies={kies}
                page={pagina}
                onPageChange={(next) =>
                  void navigate({
                    search: (previous) => ({ ...previous, pagina: next }),
                    ...keepScrollPosition,
                  })
                }
              />
            }
            detail={
              korps ? (
                <Card>
                  <CardContent className="pt-6">
                    <InschrijvingDetail
                      seizoenId={seizoen.id}
                      korps={korps}
                      named
                      periodeId={periodeId}
                      onKiesPeriode={kiesPeriode}
                    />
                  </CardContent>
                </Card>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t("ploegen:page.kiesKorps")}
                </p>
              )
            }
          />
        );
      }}
    </AanmeldShell>
  );
}
